import { useContext, useMemo } from 'react';
import { GlobalStateContext, GlobalDispatchContext } from './GlobalContextProvider';

/**
 * Hook to get the centrally-fetched map render payload
 * Only re-renders when mapRenderPayload reference changes (dispatched atomically by orchestrator)
 */
export const useGlobalMapRenderPayload = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.mapRenderPayload, [state.mapRenderPayload]);
};

/**
 * Hook to get the centrally-fetched and parsed table data
 */
export const useGlobalTableApiData = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.tableApiData, [state.tableApiData]);
};

/**
 * Hook to get the current region filter
 * Only re-renders when region changes, not when other global state updates
 */
export const useGlobalRegion = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.region, [state.region]);
};

/**
 * Hook to get the current year/timeline filter
 * Only re-renders when year changes
 */
export const useGlobalYear = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.year, [state.year]);
};

/**
 * Hook to get the available years array
 * Only re-renders when availableYears changes
 */
export const useGlobalAvailableYears = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.availableYears, [state.availableYears]);
};

/**
 * Hook to get the legend data
 * Only re-renders when legend changes
 */
export const useGlobalLegend = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.legend, [state.legend]);
};

/**
 * Hook to get the pending requests count (loading state)
 * Only re-renders when pendingRequests changes
 */
export const useGlobalFetchingStatus = () => {
  const state = useContext(GlobalStateContext);
  return useMemo(() => state.pendingRequests, [state.pendingRequests]);
};

/**
 * Hook to get the dispatch function
 * Always the same reference, never triggers re-render
 */
export const useGlobalDispatch = () => {
  return useContext(GlobalDispatchContext);
};

/**
 * Hook to get the entire global state (if needed)
 * Re-renders on any state change
 */
export const useGlobalState = () => {
  return useContext(GlobalStateContext);
};
